import React, { useState } from "react";
import api, { formatApiError, uploadFile, fileUrl } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN, NAV_KEPSEK, NAV_PUSTAKA } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Save } from "lucide-react";

const NAV_BY_ROLE = { admin: NAV_ADMIN, kepala_sekolah: NAV_KEPSEK, pustaka: NAV_PUSTAKA };

export default function Profil() {
  const { user, setUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    nama: user?.nama || "",
    tempat_lahir: user?.tempat_lahir || "",
    tanggal_lahir: user?.tanggal_lahir || "",
    niy: user?.niy || "",
    alamat: user?.alamat || "",
    telp: user?.telp || "",
    foto_file_id: user?.foto_file_id || null,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = NAV_BY_ROLE[user?.role] || NAV_ADMIN;

  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const res = await uploadFile(f); setForm({ ...form, foto_file_id: res.id }); toast.success("Foto diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profil", form);
      setUser(data);
      toast.success("Profil disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={nav}>
      <PageTitle title="Profil Saya" />
      <section className="pp-card p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6 mb-6">
          {form.foto_file_id ? (
            <img src={fileUrl(form.foto_file_id)} alt="Foto" className="w-36 h-36 rounded-xl object-cover border border-slate-200" />
          ) : (
            <div className="w-36 h-36 rounded-xl bg-slate-100 flex items-center justify-center text-slate-300 text-5xl font-bold">
              {(form.nama || "?")[0]}
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-slate-900">{form.nama || "Pengguna"}</h3>
            <p className="text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
            {isAdmin && (
              <label className="pp-btn-secondary cursor-pointer mt-4">
                <Upload className="w-4 h-4" /> {uploading ? "Mengunggah…" : "Ganti Foto"}
                <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="profil-foto-upload" />
              </label>
            )}
            {!isAdmin && (
              <p className="text-xs text-slate-500 mt-3 italic">Profil hanya bisa diedit oleh Administrator.</p>
            )}
          </div>
        </div>

        {isAdmin ? (
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 pt-6 border-t border-slate-200" data-testid="profil-form">
            <div><label className="pp-label">Nama Lengkap</label><input className="pp-input" value={form.nama} onChange={(e) => setForm({...form, nama: e.target.value})} data-testid="p-nama" /></div>
            <div><label className="pp-label">NIY</label><input className="pp-input" value={form.niy} onChange={(e) => setForm({...form, niy: e.target.value})} /></div>
            <div><label className="pp-label">Tempat Lahir</label><input className="pp-input" value={form.tempat_lahir} onChange={(e) => setForm({...form, tempat_lahir: e.target.value})} /></div>
            <div><label className="pp-label">Tanggal Lahir</label><input type="date" className="pp-input" value={form.tanggal_lahir || ""} onChange={(e) => setForm({...form, tanggal_lahir: e.target.value})} /></div>
            <div><label className="pp-label">No. Telp</label><input className="pp-input" value={form.telp} onChange={(e) => setForm({...form, telp: e.target.value})} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Alamat</label><textarea rows={2} className="pp-input" value={form.alamat} onChange={(e) => setForm({...form, alamat: e.target.value})} /></div>
            <div className="sm:col-span-2 flex justify-end">
              <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="save-profil"><Save className="w-4 h-4" />{saving ? "Menyimpan…" : "Simpan Profil"}</button>
            </div>
          </form>
        ) : (
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm pt-6 border-t border-slate-200">
            {[
              ["Nama Lengkap", form.nama || "—"],
              ["NIY", form.niy || "—"],
              ["Tempat Lahir", form.tempat_lahir || "—"],
              ["Tanggal Lahir", form.tanggal_lahir || "—"],
              ["No. Telp", form.telp || "—"],
              ["Alamat", form.alamat || "—"],
            ].map(([k, v]) => (
              <div key={k}><dt className="text-xs uppercase text-slate-500">{k}</dt><dd className="text-slate-900 mt-0.5">{v}</dd></div>
            ))}
          </dl>
        )}
      </section>
    </Layout>
  );
}
